/**
 * FS-508 — helpers for deleting a diagram entity from the in-board
 * graph preview and persisting it. Pure functions so the patch shape
 * and connected-edge collection are unit-testable without mounting the
 * (worker/d3/store-heavy) EditorWrapper.
 */

/** Minimal edge shape needed to find a node's connected edges. */
export interface EdgeEndpoints {
  fromNodeId: string
  toNodeId: string
}

/** Edge shape carrying its relation — needed to find `has_label` (symbol→tag) links. */
export interface RelatedEdge extends EdgeEndpoints {
  relation?: string
}

/** Relation linking a symbol to its identifier tag (backend: add_edge(symbol, tag, 'has_label')). */
export const HAS_LABEL_RELATION = 'has_label'

/**
 * Identifier-tag node ids linked to `nodeId` via a `has_label` edge (symbol→tag).
 * Deleting a SYMBOL should also delete these — otherwise the tag survives as an orphan
 * and the agent still finds the deleted equipment by name (#8). Returns [] unless the
 * node is a symbol (deleting a tag/connection never cascades). Datasheet-derived tags
 * are a SEPARATE source (no `has_label` from this symbol) and are intentionally kept.
 */
export function linkedTagIdsForSymbol(
  nodeId: string,
  isSymbol: boolean,
  edges: Map<string, RelatedEdge>
): string[] {
  if (!isSymbol) return []
  const out: string[] = []
  for (const edge of edges.values()) {
    if (edge.relation === HAS_LABEL_RELATION && edge.fromNodeId === nodeId) out.push(edge.toNodeId)
  }
  return out
}

/** Patch shape accepted by `apiService.updateGraph` (PUT /v1/graph/:id/data). */
export interface GraphDeletePatch {
  nodes_to_add: string[]
  nodes_to_delete: string[]
  edges_to_add: Record<string, unknown>[]
  edges_to_delete: string[]
  // Delete-only patch: it never touches properties. Both fields are
  // `never[]` so the empty-only contract is explicit and symmetric — a
  // future maintainer can't accidentally add a property mutation here.
  properties_to_upsert: never[]
  properties_to_delete: never[]
}

/** Ids of every edge that touches `nodeId` as either endpoint. */
export function collectConnectedEdgeIds(
  nodeId: string,
  edges: Map<string, EdgeEndpoints>
): string[] {
  return Array.from(edges.entries())
    .filter(([, edge]) => edge.fromNodeId === nodeId || edge.toNodeId === nodeId)
    .map(([edgeId]) => edgeId)
}

/** Build the delete-only patch for a node and its connected edges. */
export function buildEntityDeletePatch(
  nodeId: string,
  connectedEdgeIds: string[]
): GraphDeletePatch {
  return {
    nodes_to_add: [],
    nodes_to_delete: [nodeId],
    edges_to_add: [],
    edges_to_delete: connectedEdgeIds,
    properties_to_upsert: [],
    properties_to_delete: [],
  }
}
